// The Chats layout's list (#141), which replaces the channel list: every text channel and DM of every server I'm in, newest
// first, like a messaging app. A search box and one chip per server narrow it; the choice of chip is remembered on
// this device. Rows are links to /s/<server>/<channel>.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ServerSummary } from "@bloom/shared";
import { chatKey, chatPreviewLine, chatTime, chatTitle, channelInitials, dmsNeedingServer, filterChats, loadChatServer, saveChatServer, type ChatRow } from "../../chatsLogic.ts";
import { useNow } from "../../timestampFormat.ts";
import { rowMenuHandlers, type OpenRowMenu } from "../ChannelRowMenu.tsx";
import { MutedIcon } from "../icons.tsx";
import { Avatar, colorFor, initials } from "../ui.tsx";
import "./chats.css";

const AVATAR = 46;

/** The row's picture: the other person (a DM), the first other person with a count (a group DM), or a circle with the channel's initials. */
function ChatAvatar({ chat, me, names, online }: { chat: ChatRow; server?: ServerSummary | undefined; me: string | null; names: Map<string, string>; online: Set<string> | undefined }) {
  if (chat.kind === "dm") {
    const others = (chat.members ?? []).filter((id) => id !== me);
    const first = others[0] ?? me ?? "";
    if (others.length <= 1) return <Avatar id={first} name={names.get(first) ?? "?"} size={AVATAR} presence={online?.has(first) ? "online" : undefined} />;
    return (
      <span className="chat-group" style={{ width: AVATAR, height: AVATAR }} aria-hidden="true">
        <span className="avatar" style={{ background: colorFor(first), width: AVATAR, height: AVATAR, fontSize: AVATAR * 0.38 }}>{initials(names.get(first) ?? "?")}</span>
        <span className="chat-count">{others.length}</span>
      </span>
    );
  }
  const text = channelInitials(chat.name);
  const fontSize = text.length >= 3 ? Math.round(AVATAR * 0.29) : text.length === 2 ? Math.round(AVATAR * 0.35) : Math.round(AVATAR * 0.42);
  const letterSpacing = text.length >= 3 ? "-0.5px" : "normal";
  return (
    <span
      className="avatar chat-channel-icon"
      style={{ width: AVATAR, height: AVATAR, fontSize, letterSpacing }}
      aria-hidden="true"
    >
      {text}
    </span>
  );
}

export function ChatList(props: {
  chats: ChatRow[];
  servers: ServerSummary[];
  me: string | null;
  /** Names of people by id: the open server's members and the polled DM people. */
  names: Map<string, string>;
  online?: Set<string>;
  /** The open server and its open conversation (undefined: none is open, e.g. the app home). */
  serverId: string;
  activeId: string | undefined;
  onOpen: (c: ChatRow) => void;
  /** The × on a DM of the open server: close it until its next message. */
  onCloseDm: (c: ChatRow) => void;
  /** A chat's menu was asked for (long press, right-click, the Menu key). Mutes go through the open server's
   * connection, so other servers' chats have none. */
  onMenu?: OpenRowMenu;
}) {
  const { chats, servers, me, names, online, serverId, activeId, onOpen, onCloseDm, onMenu } = props;
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState<string | null>(loadChatServer);
  const now = useNow(60_000);
  const byServer = useMemo(() => new Map(servers.map((s) => [s.id, s])), [servers]);
  const serverNames = useMemo(() => new Map(servers.map((s) => [s.id, s.name])), [servers]);
  // A remembered server I've left means All.
  const selected = chip && byServer.has(chip) ? chip : null;
  const shown = useMemo(() => filterChats(chats, { query, serverId: selected, me, names, serverNames }), [chats, query, selected, me, names, serverNames]);
  // The server's name shows beside a DM only when another listed DM has the same title; a text channel always shows it.
  const listed = useMemo(() => filterChats(chats, { query: "", serverId: selected, me, names, serverNames }), [chats, selected, me, names, serverNames]);
  const dmWithServer = useMemo(() => dmsNeedingServer(listed, me, names), [listed, me, names]);
  const pick = (id: string | null) => {
    setChip(id);
    saveChatServer(id);
  };

  return (
    <div className="chat-list">
      <div className="chat-tools">
        <div className="chat-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <label htmlFor="chat-search" className="sr-only">Search chats</label>
          <input
            id="chat-search"
            type="search"
            value={query}
            placeholder="Search"
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && query) {
                e.preventDefault();
                e.stopPropagation();
                setQuery("");
              }
            }}
          />
        </div>
        {servers.length > 1 && (
          <div className="chat-chips" role="group" aria-label="Show chats from">
            <button type="button" className={`chat-chip${selected === null ? " on" : ""}`} aria-pressed={selected === null} onClick={() => pick(null)}>All</button>
            {servers.map((s) => (
              <button key={s.id} type="button" className={`chat-chip${selected === s.id ? " on" : ""}`} aria-pressed={selected === s.id} onClick={() => pick(s.id)}>{s.name}</button>
            ))}
          </div>
        )}
      </div>
      <nav aria-label="Chats">
        {shown.map((c) => {
          const server = byServer.get(c.serverId);
          const active = c.serverId === serverId && c.channelId === activeId;
          const title = chatTitle(c, me, names);
          const unread = c.unread && !active && !c.muted;
          const mentions = active ? 0 : c.mentions;
          const line = chatPreviewLine(c, me, names);
          const canClose = c.kind === "dm" && c.serverId === serverId;
          return (
            <div key={chatKey(c)} className="chat-item">
              <Link
                to={`/s/${c.serverId}/${c.channelId}`}
                onClick={() => onOpen(c)}
                className={`chat-row${active ? " active" : ""}${unread ? " unread" : ""}${c.muted ? " muted" : ""}`}
                aria-current={active ? "page" : undefined}
                title={c.kind === "dm" ? `${title} · ${server?.name ?? ""}` : `#${title} · ${server?.name ?? ""}`}
                {...(onMenu && c.serverId === serverId ? rowMenuHandlers(onMenu, c.channelId) : {})}
              >
                <span className="chat-dot-col">{unread && <span className="chat-dot" aria-label="unread" />}</span>
                <ChatAvatar chat={c} server={server} me={me} names={names} online={c.serverId === serverId ? online : undefined} />
                <span className="chat-body">
                  <span className="chat-top">
                    <span className="chat-name">{title}</span>
                    {(c.kind !== "dm" || dmWithServer.has(chatKey(c))) && <span className="chat-server">{server?.name}</span>}
                    {c.lastTs !== null && <time className="chat-time" dateTime={new Date(c.lastTs).toISOString()}>{chatTime(c.lastTs, now)}</time>}
                  </span>
                  <span className="chat-bottom">
                    <span className="chat-preview">{line}</span>
                    {c.muted && <MutedIcon className="chat-muted" />}
                    {mentions > 0 && <span className="chat-badge" aria-label={`${mentions} unread mention${mentions === 1 ? "" : "s"}`}>{mentions > 99 ? "99+" : mentions}</span>}
                  </span>
                </span>
              </Link>
              {/* Beside the link, not in it: a button inside a link would open the chat too. */}
              {canClose && <button type="button" className="icon-btn chat-close" onClick={() => onCloseDm(c)} aria-label={`Close conversation with ${title}`} title="Close">×</button>}
            </div>
          );
        })}
        {shown.length === 0 && <p className="chat-empty muted">{chats.length === 0 ? "No chats yet." : "No chats match."}</p>}
      </nav>
    </div>
  );
}
